
import React, { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import AuthLeftPanel from "@/components/Auth/AuthLeftPanel";
import { validatePassword } from "@/utils/inputSanitizer";

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Detectar o tipo de operação baseado nos parâmetros da URL
  const typeFromQuery = searchParams.get("type");
  const isPasswordRecovery = typeFromQuery === "recovery";
  
  // Note: Token handling is now managed securely through Supabase client
  // Direct localStorage token storage has been removed for security

  // Reseta mensagem de erro ao digitar
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrorMsg(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (form.password !== form.confirmPassword) {
      setErrorMsg("As senhas não coincidem.");
      return;
    }
    
    const passwordValidation = validatePassword(form.password);
    if (!passwordValidation.isValid) {
      setErrorMsg(passwordValidation.message || "Senha inválida.");
      return;
    }

    setLoading(true);

    try {
      // Usa o método de atualizar senha (usuário está autenticado via token do link)
      const { error } = await supabase.auth.updateUser({ password: form.password });
      if (error) {
        setErrorMsg(error.message || "Não foi possível definir sua senha.");
        toast({
          variant: "destructive",
          title: "Erro ao cadastrar senha",
          description: error.message || "Ocorreu um erro inesperado.",
        });
      } else {
        setSuccess(true);
        toast({
          title: "Senha definida com sucesso!",
          description: "Você já pode acessar o sistema.",
        });
        setTimeout(() => {
          navigate("/portal");
        }, 2000);
      }
    } catch (err: any) {
      setErrorMsg(err.message || "Erro inesperado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row">
      <AuthLeftPanel />
      <div className="flex-1 flex items-center justify-center bg-white min-h-screen md:min-h-0">
        <div className="w-full max-w-md p-8 md:py-16 md:px-8 shadow-none md:shadow-xl md:rounded-xl border-none bg-white animate-fade-in">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-1 text-zinc-900">
              {isPasswordRecovery ? "Redefinir senha" : "Defina sua senha"}
            </h2>
            <p className="text-gray-600 text-sm">
              {isPasswordRecovery 
                ? "Digite uma nova senha para sua conta." 
                : "Por favor, crie uma senha para acessar o sistema."
              }
            </p>
          </div>
          {success && (
            <Alert className="mb-2">
              <AlertTitle>Sucesso!</AlertTitle>
              <AlertDescription>
                Senha definida. Redirecionando...
              </AlertDescription>
            </Alert>
          )}
          {errorMsg && (
            <Alert variant="destructive" className="mb-2">
              <AlertTitle>Erro</AlertTitle>
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">Nova senha</Label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  name="password"
                  id="password"
                  type="password"
                  placeholder="Digite a nova senha"
                  className="pl-10"
                  value={form.password}
                  onChange={handleInput}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirmar nova senha</Label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  name="confirmPassword"
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirme a nova senha"
                  className="pl-10"
                  value={form.confirmPassword}
                  onChange={handleInput}
                  required
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-2 rounded-md transition disabled:opacity-60" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Definir senha"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
